import { FC, useCallback, useState } from "react";

import { ALL_TIME_DATA } from "components/constants";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ArrowDownToLineIcon, LoadingDotsIcon } from "mds/icons";
import { store, useAppSelector } from "store/index";
import {
  selectCurrentSectionTitle,
  selectExtendedClassSessionSummariesForDateRange,
  selectOrgUserByUserIdMap,
  selectStudentClassSessionUserSummaries,
  userSelectors,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { toastErrorMessage } from "utils/alerts";
import { downloadCSVFile } from "utils/file";
import { generateAttendanceReportCSVFromSessions } from "utils/reporting";

export const AttendanceReportButton: FC = () => {
  const sectionTitle = useAppSelector(selectCurrentSectionTitle);
  const allVisibleClassSessions = useListSelector(
    (state) => selectExtendedClassSessionSummariesForDateRange(state, ALL_TIME_DATA),
    ["id", "started_at", "ended_at", "effective_ended_at"],
  );
  const studentClassSessionUserSummaries = useListSelector(selectStudentClassSessionUserSummaries, [
    "user_id",
    "class_session_id",
    "presence_minutes",
    "first_seen",
    "last_seen",
    "attendance_status",
  ]);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const handleDownloadAttendanceReport = useCallback(() => {
    if (!allVisibleClassSessions || allVisibleClassSessions.length === 0) {
      toastErrorMessage(t("session_log_view.export.no_data"));
      return;
    }

    if (!sectionTitle) {
      toastErrorMessage(t("session_log_view.export.failed"));
      return;
    }

    setIsGeneratingReport(true);

    try {
      // Full entity maps only needed at click time for multi-user CSV lookups.
      const state = store.getState();
      const users = userSelectors.selectEntities(state);
      const orgUsersMap = selectOrgUserByUserIdMap(state);
      const { csvData, filename } = generateAttendanceReportCSVFromSessions(
        allVisibleClassSessions,
        users,
        orgUsersMap,
        sectionTitle,
        studentClassSessionUserSummaries,
      );
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("session_log_view.export.failed"));
    } finally {
      setIsGeneratingReport(false);
    }
  }, [allVisibleClassSessions, sectionTitle, studentClassSessionUserSummaries]);

  return (
    <SquareButton
      disabled={
        !allVisibleClassSessions.length ||
        !sectionTitle ||
        !studentClassSessionUserSummaries.length ||
        isGeneratingReport
      }
      kind="tertiary"
      size="s"
      onClick={handleDownloadAttendanceReport}
    >
      <IconText
        iconStart={isGeneratingReport ? <LoadingDotsIcon /> : <ArrowDownToLineIcon />}
        text={t("session_log_view.attendance_report.download_button")}
      />
    </SquareButton>
  );
};
